import { useEffect, useState } from 'react';
import {
	Box,
	CircularProgress,
	Dialog,
	Grid2,
	IconButton,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	ChatBotBubbleIcon,
	ChatConsultant,
	ChevronDownIcon,
	CloseIcon,
	DownloadIcon,
	EyeIcon,
} from '../assets/icons/Icons';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';
import {
	formatLlmCost,
	getMediaType,
	isBlobUrl,
	truncateForPreview,
} from '../Utils/Helpers';
import { downloadMediaFile } from '../services/ApiService';
import { getAgencyId, getUserId } from '../Utils/AuthUtil';
import { TaggedMessage } from '../types';

interface ChatDataProps {
	position: string;
	text: string;
	chatTime: string;
	fileName?: string;
	fileUrl?: string;
	onDownload?: (url: string) => void;
	caption?: string;
	chatOwner: string;
	isDownloading?: boolean;
	downloadingFile?: string;
	isThisReplyMsg?: boolean;
	repliedMsg?: string;
	repliedMsgOwner?: string;
	isMsgHasReaction?: boolean;
	reaction?: string;
	isFileIsImage?: boolean;
	isFileIsVideo?: boolean;
	imageURL?: string;
	wamid?: string;
	llmCost?: number | string;
	onReply?: (message: TaggedMessage) => void;
	isReplyDisabled?: boolean;
	onMarkUnread?: (wamid: string) => void;
}

function EnlargedImageDialog({
	open,
	imageUrl,
	fileName,
	onClose,
}: {
	open: boolean;
	imageUrl: string;
	fileName?: string;
	onClose: () => void;
}) {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			sx={{
				'& .MuiDialog-paper': {
					backgroundColor: 'transparent',
					boxShadow: 'none',
					overflow: 'visible',
				},
			}}>
			<Box
				sx={{
					position: 'relative',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					p: { xs: 1, sm: 2 },
				}}>
				<IconButton
					aria-label="Close image preview"
					onClick={onClose}
					sx={{
						position: 'absolute',
						top: { xs: -8, sm: -12 },
						right: { xs: -8, sm: -12 },
						zIndex: 1,
						backgroundColor: '#FFFFFF',
						boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
						'&:hover': {
							backgroundColor: '#F2F4F5',
						},
					}}>
					<CloseIcon />
				</IconButton>
				<Box
					component="img"
					src={imageUrl}
					alt={fileName || 'Enlarged media'}
					sx={{
						maxWidth: '92vw',
						maxHeight: '86vh',
						width: 'auto',
						height: 'auto',
						objectFit: 'contain',
						borderRadius: '8px',
						backgroundColor: '#FFFFFF',
					}}
				/>
			</Box>
		</Dialog>
	);
}

function ClickableImagePreview({
	imageUrl,
	fileName,
	isSticker,
	maxHeight,
}: {
	imageUrl: string;
	fileName?: string;
	isSticker?: boolean;
	maxHeight: string;
}) {
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);

	return (
		<>
			<Tooltip title="View image">
				<Box
					component="button"
					type="button"
					aria-label="View enlarged image"
					onClick={() => setIsPreviewOpen(true)}
					sx={{
						position: 'relative',
						display: 'block',
						maxWidth: isSticker ? '150px' : '100%',
						p: 0,
						border: 0,
						borderRadius: '8px',
						backgroundColor: 'transparent',
						cursor: 'zoom-in',
						overflow: 'hidden',
						'&:hover .image-preview-overlay, &:focus-visible .image-preview-overlay':
							{
								opacity: 1,
							},
						'&:focus-visible': {
							outline: `2px solid ${MAIN_THEME_COLOR}`,
							outlineOffset: '2px',
						},
					}}>
					<Box
						component="img"
						src={imageUrl}
						alt={fileName || 'Media'}
						sx={{
							maxWidth: isSticker ? '150px' : '100%',
							maxHeight,
							width: 'auto',
							height: 'auto',
							borderRadius: '8px',
							display: 'block',
						}}
					/>
					<Box
						className="image-preview-overlay"
						sx={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							backgroundColor: 'rgba(0, 0, 0, 0.35)',
							opacity: { xs: 1, sm: 0 },
							transition: 'opacity 160ms ease',
						}}>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								width: 36,
								height: 36,
								borderRadius: '50%',
								backgroundColor: 'rgba(255, 255, 255, 0.92)',
							}}>
							<EyeIcon color={MAIN_THEME_COLOR} />
						</Box>
					</Box>
				</Box>
			</Tooltip>
			<EnlargedImageDialog
				open={isPreviewOpen}
				imageUrl={imageUrl}
				fileName={fileName}
				onClose={() => setIsPreviewOpen(false)}
			/>
		</>
	);
}

function MediaPreview({
	fileUrl,
	position,
	fileName,
}: {
	fileUrl: string;
	position: string;
	fileName?: string;
}) {
	const [url, setUrl] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const mediaData = (fileUrl || '').split('+');
	const mediaId = mediaData[1];
	let mimeType = mediaData[2] || '';

	// Fallback MIME type from fileName extension if mimeType is missing
	if (!mimeType && fileName) {
		const ext = fileName.split('.').pop()?.toLowerCase();
		switch (ext) {
			case 'jpg':
			case 'jpeg':
				mimeType = 'image/jpeg';
				break;
			case 'png':
				mimeType = 'image/png';
				break;
			case 'webp':
				mimeType = 'image/webp';
				break;
			case 'pdf':
				mimeType = 'application/pdf';
				break;
			case 'mp4':
				mimeType = 'video/mp4';
				break;
			case 'mp3':
				mimeType = 'audio/mpeg';
				break;
		}
	}

	const mediaType = getMediaType(mimeType);

	useEffect(() => {
		let objectUrl: string | null = null;

		async function fetchMedia() {
			if (!mediaId) return;
			setLoading(true);
			const userId = getUserId();
			const orgId = getAgencyId();
			try {
				const response = await downloadMediaFile(
					mediaId,
					userId || '',
					orgId || '',
				);
				if (!response.error && response.data) {
					const blob = new Blob([response.data], {
						type: mimeType === 'sticker' ? 'image/webp' : mimeType,
					});
					objectUrl = URL.createObjectURL(blob);
					setUrl(objectUrl);
				}
			} catch (error) {
				console.error('Error fetching media:', error);
			} finally {
				setLoading(false);
			}
		}

		if (mediaType !== 'file' && mediaType !== 'pdf' && mediaId) {
			fetchMedia();
		}

		return () => {
			if (objectUrl) {
				URL.revokeObjectURL(objectUrl);
			}
		};
	}, [mediaId, mimeType, mediaType]);

	if (loading) {
		return (
			<CircularProgress
				size={24}
				sx={{ color: position === 'left' ? '#000000' : '#FFFFFF' }}
			/>
		);
	}

	if (!url) {
		if (
			mediaType === 'image' ||
			mediaType === 'audio' ||
			mediaType === 'video'
		) {
			return null;
		}
	}

	switch (mediaType) {
		case 'image': {
			const isSticker =
				(fileUrl || '').split('+')[2] === 'sticker' ||
				(fileName || '').toLowerCase().includes('sticker');
			return (
				<ClickableImagePreview
					imageUrl={url || ''}
					fileName={fileName}
					isSticker={isSticker}
					maxHeight={isSticker ? '150px' : '300px'}
				/>
			);
		}
		case 'audio':
			return (
				<audio
					controls
					src={url || ''}
					style={{ maxWidth: '100%', marginTop: '0px' }}
				/>
			);
		case 'video':
			return (
				<video
					controls
					src={url || ''}
					style={{
						maxWidth: '100%',
						maxHeight: '300px',
						borderRadius: '8px',
					}}
				/>
			);
		default:
			return null;
	}
}

function ChatBubble(data: ChatDataProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [isHovered, setIsHovered] = useState(false);
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

	function handleMenuIconClick(event: React.MouseEvent<HTMLElement>) {
		setMenuAnchorEl(event.currentTarget);
	}

	function handleMenuClose() {
		setMenuAnchorEl(null);
	}

	function handleReplyOptionClick() {
		if (data.isReplyDisabled) {
			return;
		}

		handleMenuClose();

		if (!data.onReply) {
			return;
		}

		data.onReply({
			text: truncateForPreview(
				data.fileUrl ? data.caption || data.fileName || '' : data.text,
				65,
			),
			fileName: data.fileName,
			isFile: Boolean(data.fileUrl),
			chatOwner: data.chatOwner,
			wamid: data.wamid,
		});
	}

	function handleMarkUnreadOptionClick() {
		handleMenuClose();

		if (!data.onMarkUnread || !data.wamid) {
			return;
		}

		data.onMarkUnread(data.wamid);
	}

	function getChatBubbleStyle() {
		if (data.position === 'left') {
			return {
				borderTopRightRadius: '24px',
				borderBottomLeftRadius: '24px',
				borderBottomRightRadius: '24px',
			};
		} else {
			return {
				borderTopLeftRadius: '24px',
				borderBottomLeftRadius: '24px',
				borderBottomRightRadius: '24px',
			};
		}
	}

	function isMessageIsTooLong() {
		const textLength = data.text.length;
		return textLength > 40;
	}

	function getChatOwnerTag(type: string) {
		if (type === 'consultant') {
			return 'Consultant';
		} else {
			return 'AI Assistant';
		}
	}

	function getChatIcon(type: string) {
		if (type === 'consultant') {
			return <ChatConsultant />;
		} else if (type === 'ai') {
			return <ChatBotBubbleIcon />;
		} else {
			return <></>;
		}
	}

	function getBubbleWidth() {
		return !isMobile
			? isMessageIsTooLong() ||
				data.isThisReplyMsg ||
				Boolean(data.fileUrl) ||
				data.isFileIsImage ||
				data.isFileIsVideo
				? '40%'
				: 'fit-content'
			: '80%';
	}

	const fileMimeType =
		(data.fileUrl || '').split('+')[2] ||
		(data.fileUrl || '').split('+')[1] ||
		'';
	const mediaType = getMediaType(fileMimeType);
	const isSticker =
		fileMimeType === 'sticker' ||
		(data.fileName || '').toLowerCase().includes('sticker');
	const shouldShowAttachmentRow =
		Boolean(data.fileUrl) &&
		!isSticker &&
		(mediaType === 'file' ||
			mediaType === 'pdf' ||
			mediaType === 'image' ||
			Boolean(data.imageURL));

	const replyButton =
		data.onReply || data.onMarkUnread ? (
			<>
				<IconButton
					size="small"
					disableRipple
					aria-label="Message options"
					title="Message options"
					onClick={handleMenuIconClick}
					sx={{
						position: 'absolute',
						top: '6px',
						right: '6px',
						padding: '2px',
						opacity: isHovered || isMobile ? 1 : 0,
						transition: 'opacity 120ms ease',
						backgroundColor: 'transparent',
						'&:hover': {
							backgroundColor: 'transparent',
						},
					}}>
					<ChevronDownIcon
						color={data.position === 'left' ? '#5B5B5B' : '#FFFFFF'}
					/>
				</IconButton>
				<Menu
					anchorEl={menuAnchorEl}
					open={Boolean(menuAnchorEl)}
					onClose={handleMenuClose}
					anchorOrigin={{
						vertical: 'top',
						horizontal:
							data.position === 'right' ? 'left' : 'right',
					}}
					transformOrigin={{
						vertical: 'bottom',
						horizontal:
							data.position === 'right' ? 'left' : 'right',
					}}>
					{data.onReply && (
						<Tooltip
							title={
								data.isReplyDisabled
									? 'Enable consultant mode to reply'
									: ''
							}
							placement={
								data.position === 'right' ? 'left' : 'right'
							}>
							<MenuItem
								aria-disabled={data.isReplyDisabled}
								onClick={handleReplyOptionClick}
								sx={{
									fontSize: '13px',
									minHeight: 'auto',
									py: '6px',
									...(data.isReplyDisabled && {
										opacity: 0.38,
										cursor: 'not-allowed',
										'&:hover': {
											backgroundColor: 'transparent',
										},
									}),
								}}>
								Reply
							</MenuItem>
						</Tooltip>
					)}
					{data.onMarkUnread && (
						<MenuItem
							disabled={!data.wamid}
							onClick={handleMarkUnreadOptionClick}
							sx={{
								fontSize: '13px',
								minHeight: 'auto',
								py: '6px',
							}}>
							Mark as Unread
						</MenuItem>
					)}
				</Menu>
			</>
		) : null;

	return (
		<Grid2
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			sx={{
				alignItems: 'flex-end',
				display: 'flex',
				gap: '6px',
				justifyContent:
					data.position === 'right' ? 'flex-end' : 'flex-start',
				width: '100%',
			}}>
			<Grid2
				component={'section'}
				sx={{
					position: 'relative',
					...getChatBubbleStyle(),
					display: 'inline-block',
					minWidth: 0,
					wordBreak: 'break-word',
					paddingLeft: '20px',
					paddingRight:
						data.onReply || data.onMarkUnread ? '28px' : '20px',
					paddingTop:
						data.onReply || data.onMarkUnread ? '20px' : '10px',
					paddingBottom: '10px',
					marginX: '5px',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor:
						data.position === 'left' ? '#F2F4F5' : MAIN_THEME_COLOR,
					marginTop: {
						xs: '0px',
						sm: data.position === 'right' ? '15px' : '0px',
					},
					width: getBubbleWidth(),
					maxWidth: !isMobile ? '70%' : '90%',
				}}>
				{replyButton}
				{data.position === 'right' && (
					<Grid2
						container
						sx={{
							flexDirection: 'row',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: '8px',
						}}
						paddingY={'5px'}>
						<Typography
							variant="body2"
							textAlign={'left'}
							color={'#E2FAFB'}
							fontWeight={'700'}>
							{getChatOwnerTag(data.chatOwner)}
						</Typography>
						{data.chatOwner !== 'consultant' &&
							formatLlmCost(data.llmCost) && (
								<Typography
									fontSize={'10px'}
									fontWeight={'600'}
									color={'#E2FAFB'}>
									{formatLlmCost(data.llmCost)}
								</Typography>
							)}
					</Grid2>
				)}
				{data.isThisReplyMsg && (
					<Grid2
						sx={{
							backgroundColor:
								data.position === 'left'
									? 'rgba(0, 0, 0, 0.06)'
									: 'rgba(0, 0, 0, 0.15)',
							borderRadius: 1,
							px: 1.5,
							py: 1,
							mb: 1,
							justifyContent: 'center',
							borderLeft: `5px solid ${data.position === 'left' ? MAIN_THEME_COLOR : '#F2F4F5'}`,
						}}>
						{data.repliedMsgOwner && (
							<Typography
								fontSize={'11px'}
								fontWeight={'600'}
								color={
									data.position === 'left'
										? MAIN_THEME_COLOR
										: '#F2F4F5'
								}>
								{data.repliedMsgOwner === 'human'
									? 'Customer'
									: 'Consultant'}
							</Typography>
						)}
						{isBlobUrl(data.repliedMsg || '') ? (
							<img
								src={data.repliedMsg}
								alt="Media"
								style={{
									maxWidth: '100%',
									maxHeight: '120px',
									width: 'auto',
									height: 'auto',
									borderRadius: 8,
									display: 'block',
								}}
							/>
						) : (
							<Typography
								style={{
									overflowWrap: 'break-word',
									whiteSpace: 'pre-line',
									fontSize: '11px',
								}}
								color={
									data.position === 'left'
										? '#000000'
										: '#FFFFFF'
								}>
								{data.repliedMsg}
							</Typography>
						)}
					</Grid2>
				)}
				{data.fileUrl ? (
					<Grid2 container flexDirection={'column'}>
						{data.isFileIsImage && data.imageURL ? (
							<ClickableImagePreview
								imageUrl={data.imageURL}
								fileName={data.fileName}
								maxHeight="250px"
							/>
						) : data.isFileIsVideo && data.imageURL ? (
							<video
								src={data.imageURL}
								controls
								style={{
									maxWidth: '100%',
									maxHeight: '250px',
									width: 'auto',
									height: 'auto',
									borderRadius: 8,
									display: 'block',
								}}
							/>
						) : (
							<MediaPreview
								fileUrl={data.fileUrl}
								position={data.position}
								fileName={data.fileName}
							/>
						)}
						{shouldShowAttachmentRow ? (
							<Grid2
								container
								flexDirection={'row'}
								width={'100%'}
								alignItems={'center'}
								sx={{
									backgroundColor:
										data.position === 'left'
											? '#F0F0F0'
											: MAIN_THEME_COLOR,
									padding: '5px',
									borderRadius: '8px',
									boxShadow:
										!data.isFileIsImage &&
										!data.isFileIsVideo
											? '0 2px 2px rgba(0, 0, 0, 0.1)'
											: '',
								}}>
								<Typography
									variant="body2"
									style={{
										overflowWrap: 'break-word',
										maxWidth: isMobile
											? 'calc(90% - 10px)'
											: 'calc(98% - 10px)',
									}}
									color={
										data.position === 'left'
											? '#000000'
											: '#FFFFFF'
									}>
									{!data.isFileIsImage && data.fileName}
								</Typography>
								{data.isDownloading &&
								data.downloadingFile === data.fileUrl ? (
									<CircularProgress
										sx={{
											color:
												data.position === 'left'
													? '#28696D'
													: '#FFFFFF',
											marginLeft: '5px',
											padding: '2px',
										}}
										size={24}
									/>
								) : (
									<IconButton
										sx={{
											marginLeft: '5px',
											padding: '2px',
										}}
										onClick={() =>
											data.onDownload &&
											data.onDownload(
												data.fileUrl
													? data.fileUrl
													: '',
											)
										}>
										<DownloadIcon
											color={
												data.position === 'left'
													? MAIN_THEME_COLOR
													: '#FFFFFF'
											}
										/>
									</IconButton>
								)}
							</Grid2>
						) : null}
						{data.caption && (
							<Typography
								variant="body2"
								style={{
									overflowWrap: 'break-word',
									whiteSpace: 'pre-line',
								}}
								sx={{
									color:
										data.position === 'left'
											? '#000000'
											: '#FFFFFF',
									maxWidth: '100%',
									paddingTop: '5px',
									paddingLeft: '5px',
								}}>
								{data.caption}
							</Typography>
						)}
					</Grid2>
				) : (
					<Typography
						variant="body2"
						style={{
							overflowWrap: 'break-word',
							whiteSpace: 'pre-line',
						}}
						color={
							data.position === 'left' ? '#000000' : '#FFFFFF'
						}>
						{data.text}
					</Typography>
				)}
				<Typography
					fontSize={'8px'}
					textAlign={data.position === 'left' ? 'start' : 'end'}
					color={data.position === 'left' ? '#000000' : '#FFFFFF'}
					paddingTop={'5px'}>
					{data.chatTime}
				</Typography>
			</Grid2>
			{data.position === 'right' && getChatIcon(data.chatOwner)}
		</Grid2>
	);
}

function ChatRow(chat: ChatDataProps) {
	return (
		<Grid2
			container
			justifyContent={
				chat.position === 'right' ? 'flex-end' : 'flex-start'
			}
			padding={'10px'}
			width="100%">
			<ChatBubble
				position={chat.position}
				text={chat.text}
				chatTime={chat.chatTime}
				fileName={chat.fileName}
				fileUrl={chat.fileUrl}
				onDownload={chat.onDownload}
				caption={chat.caption}
				chatOwner={chat.chatOwner}
				isDownloading={chat.isDownloading}
				downloadingFile={chat.downloadingFile}
				isThisReplyMsg={chat.isThisReplyMsg}
				repliedMsg={chat.repliedMsg}
				repliedMsgOwner={chat.repliedMsgOwner}
				isMsgHasReaction={chat.isMsgHasReaction}
				reaction={chat.reaction}
				isFileIsImage={chat.isFileIsImage}
				isFileIsVideo={chat.isFileIsVideo}
				imageURL={chat.imageURL}
				wamid={chat.wamid}
				llmCost={chat.llmCost}
				onReply={chat.onReply}
				isReplyDisabled={chat.isReplyDisabled}
				onMarkUnread={chat.onMarkUnread}
			/>
		</Grid2>
	);
}

export default ChatRow;
